import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	useCreateOrganization,
	useOrganizations,
	useOrganizationWorkspace,
} from "@/hooks/query/organization";
import type { ActiveOrganization, Organization } from "@/lib/auth";
import { useSession } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { MembersList } from "./-components/members-list";
import { NoOrganizationEmpty } from "./-components/no-organization-empty";
import { OrganizationForm } from "./-components/organization-form";

export const Route = createFileRoute("/(app)/settings/organization")({
	component: RouteComponent,
});

function RouteComponent() {
	const { isPending: isSessionPending, data: sessionResponse } = useSession();
	const [organizationName, setOrganizationName] = useState("");

	const { data: organizations = [], isPending: isOrganizationsPending } = useOrganizations();

	const organization = organizations[0] as Organization | undefined;
	const organizationId = organization?.id ?? "";

	const { data: organizationWorkspace, isPending: isWorkspacePending } =
		useOrganizationWorkspace(organizationId);

	const createOrganizationMutation = useCreateOrganization();

	const workspace = organizationWorkspace as ActiveOrganization | undefined;
	const members = workspace?.members ?? [];

	const currentUserRole = members.find(
		(member) => member.userId === sessionResponse?.user.id,
	)?.role;
	const isOwner = currentUserRole === "owner";

	const handleCreateOrganization = useCallback(() => {
		if (!organizationName.trim()) return;
		createOrganizationMutation.mutate(
			{ name: organizationName.trim() },
			{ onSuccess: () => setOrganizationName("") },
		);
	}, [organizationName, createOrganizationMutation]);

	if (isSessionPending || isOrganizationsPending) {
		return (
			<div className="p-6 text-sm text-muted-foreground">
				{m.organizationSettings_loadingWorkspace()}
			</div>
		);
	}

	if (!organization) {
		return (
			<div className="space-y-6">
				<NoOrganizationEmpty />
				<div className="mx-auto w-full max-w-xl rounded-lg border p-6">
					<div className="space-y-2">
						<h3 className="text-lg font-semibold">
							{m.organizationSettings_createFirstTitle()}
						</h3>
						<p className="text-muted-foreground text-sm">
							{m.organizationSettings_createFirstDescription()}
						</p>
					</div>
					<div className="mt-4 flex gap-2">
						<Input
							value={organizationName}
							onChange={(event) => setOrganizationName(event.target.value)}
							placeholder={m.organizationSettings_organizationNamePlaceholder()}
						/>
						<Button
							disabled={
								!organizationName.trim() || createOrganizationMutation.isPending
							}
							onClick={handleCreateOrganization}
						>
							{createOrganizationMutation.isPending
								? m.organizationSettings_creating()
								: m.organizationSettings_createOrganization()}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<Tabs defaultValue="organization">
			<TabsList variant="line" className="w-full">
				<TabsTrigger value="organization" className="flex-1">
					{m.organizationSettings_tabOrganization()}
				</TabsTrigger>
				<TabsTrigger value="members" className="flex-1">
					{m.organizationSettings_tabMembers()}
				</TabsTrigger>
			</TabsList>

			<TabsContent value="organization">
				<div className="mt-6">
					<OrganizationForm organization={organization} isOwner={isOwner} />
				</div>
			</TabsContent>

			<TabsContent value="members">
				<div className="mt-6">
					{isWorkspacePending ? (
						<div className="text-muted-foreground rounded-lg border p-6 text-sm">
							{m.organizationSettings_loadingMembers()}
						</div>
					) : (
						<MembersList
							members={members}
							invitations={workspace?.invitations ?? []}
							organizationId={organizationId}
						/>
					)}
				</div>
			</TabsContent>
		</Tabs>
	);
}
